import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CommonModal from '../common/CommonModal';
import { useTranslation } from 'react-i18next';
import { useStudents, Student } from '../../hooks/useStudents';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionClass } from '../../contexts/SessionClassContext';
import { useSessions } from '../../hooks/useSession';
import { useClasses } from '../../hooks/useClasses';

interface GetExamFormModalProps {
  open: boolean;
  onClose: () => void;
  onStudentFound: (student: Student) => void;
  isLoading?: boolean;
}

// Helper function to detect if class is 10th or 12th (for Exam Form vs Enrollment Form)
const isExamFormClass = (className?: string | null) => {
  if (!className) return false;
  const normalized = className.toLowerCase();
  const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
  const hasNumber = (n: number) => numbers.has(n);
  return (
    hasNumber(10) ||
    hasNumber(12) ||
    normalized.includes('class 10') ||
    normalized.includes('class 12') ||
    normalized.includes('10th') ||
    normalized.includes('12th') ||
    normalized.includes('tenth') ||
    normalized.includes('twelfth')
  );
};

const GetExamFormModal: React.FC<GetExamFormModalProps> = ({
  open,
  onClose,
  onStudentFound,
  isLoading: externalIsLoading = false
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const {
    selectedClass,
    setSelectedClass,
    selectedSession,
    setSelectedSession,
    selectedClassName,
  } = useSessionClass();
  const schoolId = user?.schoolId ?? undefined;
  
  // Helper function to get the appropriate form text based on selected class
  const getFormText = (examFormKey: string, enrollmentFormKey: string) => {
    const className = selectedClass?.name || selectedClassName || '';
    return isExamFormClass(className) ? t(examFormKey) : t(enrollmentFormKey);
  };
  
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { classesQuery } = useClasses(schoolId);
  const { sessionsQuery } = useSessions(schoolId);

  const classes = classesQuery.data || [];
  const sessions = sessionsQuery.data || [];

  const coerceId = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const normalizedSessionId = coerceId(selectedSession?.id);
  const normalizedClassId = coerceId(selectedClass?.id);

  const classNameFilter =
    (selectedClass as any)?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : (selectedClass as any)?.section === "__GRADE_ALL__"
        ? `${String((selectedClass as any)?.name || "").trim()}-ALL`
        : String((selectedClass as any)?.name || "").includes("-ALL")
          ? String((selectedClass as any)?.name || "")
          : null;

  const hasSessionFilter = normalizedSessionId !== null;
  const effectiveClassId = normalizedClassId != null && normalizedClassId > 0 ? normalizedClassId : null;
  const hasClassFilter = effectiveClassId !== null || !!classNameFilter;

  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;
    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;
    const numericMatch = className.match(/\d+/);
    if (numericMatch) return parseInt(numericMatch[0], 10);
    return Number.MAX_SAFE_INTEGER;
  };
  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const normalized = String(section).trim().toUpperCase();
    if (normalized.length === 0) return 0;
    const charCode = normalized.charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      return charCode - 64; // A=1, B=2, C=3, etc.
    }
    return Number.MAX_SAFE_INTEGER;
  };

  const filteredClasses = useMemo(() => {
    const base = Array.isArray(classes) ? classes : [];
    if (!base.length) return [] as any[];

    const sorted = [...base].sort((a: any, b: any) => {
      const classDiff = getClassSortValue(a?.name) - getClassSortValue(b?.name);
      if (classDiff !== 0) return classDiff;
      const sectionDiff = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
      if (sectionDiff !== 0) return sectionDiff;
      return (a?.name || "").localeCompare(b?.name || "");
    });

    const gradeNames = Array.from(new Set(sorted.map((c: any) => c?.name).filter(Boolean))) as string[];
    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

    const schoolIdFallback = sorted[0]?.schoolId ?? 0;
    const out: any[] = [{ id: "Class-ALL", name: "Class", section: "__CLASS_ALL__", schoolId: schoolIdFallback }];
    for (const grade of gradeNames) {
      out.push({ id: `${grade}-ALL`, name: grade, section: "__GRADE_ALL__", schoolId: schoolIdFallback });
      out.push(...sorted.filter((c: any) => c?.name === grade));
    }
    return out;
  }, [classes]);

  // Get students hook - use filtered API when both session and class are selected
  const { studentsQuery, useStudentsBySessionIdAndClassId, useStudentsBySessionIdAndClassName } = useStudents(false, schoolId);
  
  // Determine which query to use based on whether both session and class are selected
  const shouldUseFilteredQuery = normalizedSessionId !== null && hasClassFilter;
  
  const studentsByIdQuery = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : (shouldUseFilteredQuery ? (normalizedSessionId ?? undefined) : undefined),
    isStudent ? undefined : (shouldUseFilteredQuery ? (effectiveClassId ?? undefined) : undefined)
  );

  const studentsByNameQuery = useStudentsBySessionIdAndClassName(
    isStudent ? undefined : (shouldUseFilteredQuery ? (normalizedSessionId ?? undefined) : undefined),
    isStudent ? undefined : (shouldUseFilteredQuery ? (classNameFilter ?? undefined) : undefined)
  );

  const activeStudentsQuery = classNameFilter ? studentsByNameQuery : studentsByIdQuery;

  const {
    data: filteredStudents = [],
    isLoading: isLoadingFilteredStudents,
    error: errorFilteredStudents,
  } = activeStudentsQuery;
  
  // Fallback to all students when session/class are not selected
  const allStudents = studentsQuery.data;
  const isLoadingAllStudents = studentsQuery.isLoading;
  const errorAllStudents = studentsQuery.error;

  // Use filtered students from API when both session and class are selected, otherwise filter all students
  const sortedStudents = useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (shouldUseFilteredQuery) {
      if (!filteredStudents || filteredStudents.length === 0) return [];
      
      return [...filteredStudents].sort((a, b) => {
        const getStudentName = (student: any) => {
          if (student.studentName) {
            return student.studentName;
          } else if (student.firstName && student.lastName) {
            return `${student.firstName} ${student.lastName}`;
          } else if (student.firstName) {
            return student.firstName;
          } else if (student.lastName) {
            return student.lastName;
          } else {
            return t('unknown_student');
          }
        };

        const nameA = getStudentName(a).toLowerCase();
        const nameB = getStudentName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!allStudents) return [];

    let filtered = allStudents;

    if (hasSessionFilter) {
      filtered = filtered.filter(
        (student: any) => coerceId(student.sessionId) === normalizedSessionId
      );
    }

    if (hasClassFilter) {
      filtered = filtered.filter(
        (student: any) => coerceId(student.schoolClassId) === normalizedClassId
      );
    }

    return [...filtered].sort((a, b) => {
      const getStudentName = (student: any) => {
        if (student.studentName) {
          return student.studentName;
        } else if (student.firstName && student.lastName) {
          return `${student.firstName} ${student.lastName}`;
        } else if (student.firstName) {
          return student.firstName;
        } else if (student.lastName) {
          return student.lastName;
        } else {
          return t('unknown_student');
        }
      };

      const nameA = getStudentName(a).toLowerCase();
      const nameB = getStudentName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    shouldUseFilteredQuery, 
    filteredStudents, 
    allStudents, 
    hasSessionFilter, 
    hasClassFilter, 
    normalizedSessionId, 
    normalizedClassId, 
    t
  ]);

  useEffect(() => {
    setSelectedStudentId(null);
  }, [effectiveClassId, classNameFilter, normalizedSessionId, open]);

  const handleSessionChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === '') {
        setSelectedSession(null);
        setSelectedClass(null);
        return;
      }
      const numericValue = Number(value);
      const sessionObj = sessions.find((session: any) => Number(session.id) === numericValue);
      setSelectedSession(sessionObj || null);
      // Don't clear class when session changes - preserve class selection
    },
    [sessions, setSelectedSession]
  );

  const handleClassChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === '') {
        setSelectedClass(null);
        return;
      }
      const vStr = String(value);
      const classObj = (filteredClasses || []).find((cls: any) => String(cls.id) === vStr);
      setSelectedClass(classObj || null);
    },
    [filteredClasses, setSelectedClass]
  );

  const handleStudentSelect = useCallback((event: any) => {
    const studentId = Number.parseInt(event.target.value, 10);
    setSelectedStudentId(studentId);
    setError(null); // Clear any previous errors
  }, []);

  const handleGetExamForm = useCallback(async () => {
    setError(null);

    if (!selectedStudentId || !sortedStudents) {
      setError(t('please_select_student'));
      return;
    }

    const student = sortedStudents.find((s: Student) => s.id === selectedStudentId);

    if (!student) {
      setError(t('student_not_found'));
      return;
    }

    try {
      // Since we already have the student data, we can directly call onStudentFound
      onStudentFound(student);
      onClose();
    } catch (err) {
      setError(t('failed_to_process_student_data'));
    }
  }, [selectedStudentId, sortedStudents, onStudentFound, onClose]);

  const handleClose = useCallback(() => {
    setSelectedStudentId(null);
    setError(null);
    onClose();
  }, [onClose]);

  let studentSection: React.ReactNode;
  const isLoadingStudents = shouldUseFilteredQuery ? isLoadingFilteredStudents : isLoadingAllStudents;
  const errorLoadingStudents = shouldUseFilteredQuery ? errorFilteredStudents : errorAllStudents;
  
  if (isLoadingStudents) {
    studentSection = (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>{t('loading_students')}</Typography>
      </Box>
    );
  } else if (errorLoadingStudents) {
    studentSection = (
      <Alert severity="error" sx={{ mb: 3 }}>
        {t('error_loading_students', {
          error: errorLoadingStudents instanceof Error ? errorLoadingStudents.message : 'Unknown error',
        })}
      </Alert>
    );
  } else if (!normalizedSessionId || !hasClassFilter) {
    studentSection = (
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('select_session_class_first', {
          defaultValue:
            'Use the session and class selectors above to choose a cohort before picking a student.',
        })}
      </Alert>
    );
  } else if (sortedStudents && sortedStudents.length > 0) {
    studentSection = (
      <FormControl
        fullWidth
        required
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#f8f9fb',
            borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(1,104,234,0.4)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0168EA' },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#0168EA',
            },
          },
        }}
      >
        <InputLabel id="select-student-label">
          {t('select_student')} *
        </InputLabel>
        <Select
          labelId="select-student-label"
          label={`${t('select_student')} *`}
          value={selectedStudentId || ''}
          onChange={handleStudentSelect}
          disabled={externalIsLoading}
          required
          sx={{
            '& .MuiSelect-select': {
              padding: '16.5px 14px',
              '&:focus': {
                backgroundColor: 'transparent',
              },
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: '60vh',
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  minHeight: '48px',
                  fontSize: '0.95rem',
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          <MenuItem value="">
            <em>{t('none')}</em>
          </MenuItem>
          {sortedStudents.map((student: any) => (
            <MenuItem
              key={student.id}
              value={student.id}
              sx={{
                py: 1.5,
                minHeight: '48px',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {(() => {
                    const name = student.studentName || student.firstName || 'S';
                    return name.charAt(0).toUpperCase();
                  })()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    sx={{
                      fontSize: '0.95rem',
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {(() => {
                      if (student.studentName) {
                        return student.studentName;
                      } else if (student.firstName && student.lastName) {
                        return `${student.firstName} ${student.lastName}`;
                      } else if (student.firstName) {
                        return student.firstName;
                      } else if (student.lastName) {
                        return student.lastName;
                      } else {
                        return t('unknown_student');
                      }
                    })()}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  } else {
    studentSection = (
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('no_students_found_school')}
      </Alert>
    );
  }

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={getFormText('view_exam_form', 'view_enrollment_form')}
      maxWidth="xs"
      disableEscapeKeyDown
      PaperProps={{ sx: { maxWidth: '400px', width: '90%' } }}
      customActions={
        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={externalIsLoading}
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleGetExamForm}
            variant="contained"
            disabled={externalIsLoading || !selectedStudentId || !normalizedSessionId || !hasClassFilter}
            sx={{ backgroundColor: '#3092e3', '&:hover': { backgroundColor: 'primary.main' } }}
          >
            {externalIsLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('loading')}...
              </>
            ) : (
              t('get_exam_form')
            )}
          </Button>
        </Box>
      }
    >
      <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getFormText('exam_form_modal_help_text', 'enrollment_form_modal_help_text')}
          </Typography>

          <FormControl
            fullWidth
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputLabel-root': {
                color: '#666',
                '&.Mui-focused': { color: '#0168EA' },
              },
            }}
          >
            <InputLabel>{t('session')}</InputLabel>
            <Select
              value={normalizedSessionId ?? ''}
              label={t('session')}
              onChange={handleSessionChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('select_session_placeholder', { defaultValue: 'Select Session' })}</em>
              </MenuItem>
              {sessions.map((session: any) => (
                <MenuItem key={session.id} value={session.id}>
                  {session.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputLabel-root': {
                color: '#666',
                '&.Mui-focused': { color: '#0168EA' },
              },
            }}
            disabled={!normalizedSessionId || filteredClasses.length === 0}
          >
            <InputLabel>{t('class')}</InputLabel>
            <Select
              value={(() => {
                if (!selectedClass) return '';
                if ((selectedClass as any)?.section === "__CLASS_ALL__") return "Class-ALL";
                if ((selectedClass as any)?.section === "__GRADE_ALL__")
                  return `${String((selectedClass as any)?.name || "").trim()}-ALL`;
                return selectedClass?.id != null ? String(selectedClass.id) : '';
              })()}
              label={t('class')}
              onChange={handleClassChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('select_class_placeholder', { defaultValue: 'Select Class' })}</em>
              </MenuItem>
              {filteredClasses.map((cls: any) => (
                <MenuItem key={String(cls.id)} value={String(cls.id)}>
                  {(cls as any)?.section === "__CLASS_ALL__"
                    ? "Class-ALL"
                    : (cls as any)?.section === "__GRADE_ALL__"
                      ? `${cls.name}-ALL`
                      : cls.section
                        ? `${cls.name} ${cls.section}`
                        : cls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {studentSection}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
    </CommonModal>
  );
};

export default GetExamFormModal;



